/**
 * The chart palette, and the rules it has to keep.
 *
 * Both modes are VALIDATED, not eyeballed — run through the dataviz validator
 * against this console's own surfaces (#ffffff light, #181a22 dark):
 *
 *   light: lightness band PASS · chroma PASS · CVD ΔE 9.1 PASS · normal ΔE 22.9 PASS
 *   dark:  lightness band PASS · chroma PASS · CVD ΔE 8.4 PASS · normal ΔE 19.8 PASS
 *
 * Light mode carries one contrast WARN — aqua and yellow sit under 3:1 on white
 * — which is not dismissable: it obliges visible labels or a table view. Both
 * ship. Every chart has a legend, hover values, and a table toggle, so identity
 * is never carried by colour alone.
 *
 * Hues are assigned in FIXED ORDER and never cycled. A ninth series does not get
 * a generated colour; the panels here are shaped so there is never a ninth.
 */
export const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
export const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];

export const seriesColors = (dark: boolean) => (dark ? SERIES_DARK : SERIES_LIGHT);

/** Formatters per unit. Bytes and seconds are the ones that read badly raw. */
export const formatValue = (unit: string, value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  switch (unit) {
    case 'bytes': {
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let size = value;
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index += 1;
      }
      return `${size >= 100 ? Math.round(size) : size.toFixed(1)}${units[index]}`;
    }
    case 'percent':
      return `${value >= 10 ? Math.round(value) : value.toFixed(1)}%`;
    case 'seconds':
      return value < 1 ? `${Math.round(value * 1000)}ms` : `${value.toFixed(2)}s`;
    case 'cores':
      return value.toFixed(2);
    case 'ops':
      return `${value >= 10 ? Math.round(value) : value.toFixed(1)}/s`;
    // Matches quietCell on the Tenants screen: a duration a reader skims should
    // not change vocabulary between the table and the chart beside it.
    case 'minutes':
      return value >= 90 ? `${Math.round(value / 60)}h` : `${Math.round(value)}m`;
    default:
      return value >= 1000 ? Math.round(value).toLocaleString() : String(Math.round(value * 100) / 100);
  }
};
